import { supabase } from "@/integrations/supabase/client";
import { LinkInBioProfile, CustomLink, SocialLink } from "@/types/linkinbio";

export class LinkInBioAPI {
  // Profile operations
  static async getProfile(userId: string): Promise<LinkInBioProfile | null> {
    // Get the business profile first to find the linked linkinbio profile
    const { data: businessProfile, error: businessError } = await supabase
      .from("business_profiles")
      .select("id, booking_url")
      .eq("user_id", userId)
      .single();

    if (businessError || !businessProfile) {
      console.error("Error fetching business profile:", businessError);
      return null;
    }

    const { data: profile, error } = await supabase
      .from("linkinbio_profiles")
      .select("id, business_profile_id, display_name, bio, avatar_url, background_type, background_value, theme, show_brand, is_public, created_at, updated_at")
      .eq("business_profile_id", businessProfile.id)
      .maybeSingle();

    if (error) {
      console.error("Error fetching linkinbio profile:", error);
      return null;
    }

    if (!profile) {
      return null;
    }

    // Get social links
    const { data: socialLinks } = await supabase
      .from("linkinbio_social_links")
      .select("id, profile_id, platform, url, username, is_active, order_index, click_count")
      .eq("profile_id", profile.id)
      .order("created_at", { ascending: true });

    // Get custom links
    const { data: customLinks } = await supabase
      .from("linkinbio_custom_links")
      .select("id, profile_id, title, url, description, icon_type, icon_value, order_index, is_active, click_count")
      .eq("profile_id", profile.id)
      .order("order_index", { ascending: true });

    return this.mapDbProfileToLinkInBioProfile(
      profile,
      socialLinks || [],
      customLinks || [],
      businessProfile.booking_url || undefined  // Use booking_url from business_profiles
    );
  }

  static async getProfileByBookingUrl(bookingUrl: string): Promise<LinkInBioProfile | null> {
    // Fetch profile via Edge Function (bypasses RLS, returns only public fields)
    const { data: response, error: functionError } = await supabase.functions.invoke(
      'get-linkinbio-profile',
      { body: { bookingUrl } }
    );

    // functionError = Edge Function returned non-2xx (404 = business doesn't exist)
    if (functionError) {
      throw new Error('Business not found');
    }

    // response.error = Edge Function returned error in response body
    if (response?.error) {
      throw new Error(response.error);
    }

    const profileData = response.profile;

    // Profile null = business exists but no link-in-bio created yet (valid state)
    // Return null (not an error) so component can redirect to booking page
    if (!profileData) {
      return null;
    }

    // Transform Edge Function response to LinkInBioProfile type
    return {
      username: profileData.username,  // = bookingUrl (source of truth)
      displayName: profileData.display_name,
      bio: profileData.bio || undefined,
      profileImage: profileData.profile_image || undefined,
      socialLinks: profileData.socialLinks.map((link: any) => ({
        platform: link.platform,
        url: link.url,
      })),
      customLinks: profileData.customLinks.map((link: any) => ({
        id: link.id,
        title: link.title,
        url: link.url,
        icon: link.icon || undefined,
        enabled: true,  // Only active links returned from Edge Function
        order: link.order_index,
      })),
      theme: profileData.theme || 'minimal',
      font: profileData.font || 'default',
      layout: profileData.layout || 'business',
      buttonStyle: profileData.button_style || 'rounded',
      backgroundColor: profileData.background_color || undefined,
      backgroundImage: profileData.background_image || undefined,
      buttonColor: profileData.button_color || undefined,
      textColor: profileData.text_color || undefined,
      showBookingButton: profileData.show_booking_button !== false,
    };
  }

  static async createProfile(
    userId: string,
    profile: Partial<LinkInBioProfile>
  ): Promise<LinkInBioProfile | null> {
    // Get the business profile
    const { data: businessProfile } = await supabase
      .from("business_profiles")
      .select("id, booking_url, name")
      .eq("user_id", userId)
      .single();

    if (!businessProfile) {
      throw new Error("Business profile not found. Please create a business profile first.");
    }

    // Check if a LinkInBio profile already exists for this business
    const { data: existing } = await supabase
      .from("linkinbio_profiles")
      .select("id")
      .eq("business_profile_id", businessProfile.id)
      .single();

    if (existing) {
      throw new Error("LinkInBio profile already exists for this business");
    }

    // Determine background type and value from profile
    let backgroundType: 'gradient' | 'solid' | 'image' = 'gradient';
    let backgroundValue = '#6366f1,#8b5cf6'; // Default gradient

    if (profile.backgroundImage) {
      backgroundType = 'image';
      backgroundValue = profile.backgroundImage;
    } else if (profile.backgroundColor) {
      // Check if it's a gradient (contains comma) or solid color
      backgroundType = profile.backgroundColor.includes(',') ? 'gradient' : 'solid';
      backgroundValue = profile.backgroundColor;
    }

    // Create profile linked to business
    const { data: newProfile, error: profileError } = await supabase
      .from("linkinbio_profiles")
      .insert({
        business_profile_id: businessProfile.id,
        display_name: profile.displayName || businessProfile.name,
        bio: profile.bio,
        avatar_url: profile.profileImage,
        background_type: backgroundType,
        background_value: backgroundValue,
        theme: profile.theme || "minimal",
        show_brand: profile.showBookingButton !== false,
        is_public: true,
      })
      .select()
      .single();

    if (profileError || !newProfile) {
      throw profileError || new Error("Failed to create profile");
    }

    // Create social links
    if (profile.socialLinks && profile.socialLinks.length > 0) {
      await supabase.from("linkinbio_social_links").insert(
        profile.socialLinks.map((link) => ({
          profile_id: newProfile.id,
          platform: link.platform,
          url: link.url,
        }))
      );
    }

    // Create custom links
    if (profile.customLinks && profile.customLinks.length > 0) {
      await supabase.from("linkinbio_custom_links").insert(
        profile.customLinks.map((link, index) => ({
          profile_id: newProfile.id,
          title: link.title,
          url: link.url,
          icon_type: link.icon ? 'custom' : 'default',
          icon_value: link.icon,
          is_active: link.enabled !== false,
          order_index: link.order ?? index,
        }))
      );
    }

    return this.getProfile(userId);
  }

  static async updateProfile(
    userId: string,
    updates: Partial<LinkInBioProfile>
  ): Promise<LinkInBioProfile | null> {
    // Get business profile first
    const { data: businessProfile } = await supabase
      .from("business_profiles")
      .select("id")
      .eq("user_id", userId)
      .single();

    if (!businessProfile) {
      throw new Error("Business profile not found");
    }

    // Get current profile
    const { data: currentProfile } = await supabase
      .from("linkinbio_profiles")
      .select("id")
      .eq("business_profile_id", businessProfile.id)
      .single();

    if (!currentProfile) {
      throw new Error("LinkInBio profile not found");
    }

    // Determine background type and value if background fields are being updated
    const updateData: any = {};

    if (updates.displayName !== undefined) updateData.display_name = updates.displayName;
    if (updates.bio !== undefined) updateData.bio = updates.bio;
    if (updates.profileImage !== undefined) updateData.avatar_url = updates.profileImage;
    if (updates.theme !== undefined) updateData.theme = updates.theme;
    if (updates.showBookingButton !== undefined) updateData.show_brand = updates.showBookingButton;

    // Handle background updates
    if (updates.backgroundImage !== undefined || updates.backgroundColor !== undefined) {
      if (updates.backgroundImage) {
        updateData.background_type = 'image';
        updateData.background_value = updates.backgroundImage;
      } else if (updates.backgroundColor) {
        updateData.background_type = updates.backgroundColor.includes(',') ? 'gradient' : 'solid';
        updateData.background_value = updates.backgroundColor;
      }
    }

    // Update profile (note: username changes should be done via business_profile booking_url)
    const { error: profileError } = await supabase
      .from("linkinbio_profiles")
      .update(updateData)
      .eq("id", currentProfile.id);

    if (profileError) {
      throw profileError;
    }

    // Update social links if provided
    if (updates.socialLinks) {
      // Delete existing social links
      await supabase
        .from("linkinbio_social_links")
        .delete()
        .eq("profile_id", currentProfile.id);

      // Insert new social links
      if (updates.socialLinks.length > 0) {
        await supabase.from("linkinbio_social_links").insert(
          updates.socialLinks.map((link) => ({
            profile_id: currentProfile.id,
            platform: link.platform,
            url: link.url,
          }))
        );
      }
    }

    // Update custom links if provided
    if (updates.customLinks) {
      // Delete existing custom links
      await supabase
        .from("linkinbio_custom_links")
        .delete()
        .eq("profile_id", currentProfile.id);

      // Insert new custom links
      if (updates.customLinks.length > 0) {
        await supabase.from("linkinbio_custom_links").insert(
          updates.customLinks.map((link, index) => ({
            profile_id: currentProfile.id,
            title: link.title,
            url: link.url,
            icon_type: link.icon ? 'custom' : 'default',
            icon_value: link.icon,
            is_active: link.enabled !== false,
            order_index: link.order ?? index,
          }))
        );
      }
    }

    return this.getProfile(userId);
  }

  static async deleteProfile(userId: string): Promise<boolean> {
    // Get business profile first
    const { data: businessProfile } = await supabase
      .from("business_profiles")
      .select("id")
      .eq("user_id", userId)
      .single();

    if (!businessProfile) {
      return false;
    }

    const { error } = await supabase
      .from("linkinbio_profiles")
      .delete()
      .eq("business_profile_id", businessProfile.id);

    return !error;
  }

  // Analytics operations
  static async trackLinkClick(linkId: string, metadata?: {
    ipAddress?: string;
    userAgent?: string;
    referrer?: string;
  }): Promise<void> {
    await supabase.from("linkinbio_analytics").insert({
      link_id: linkId,
      ip_address: metadata?.ipAddress,
      user_agent: metadata?.userAgent,
      referrer: metadata?.referrer,
    });
  }

  static async getLinkAnalytics(
    userId: string,
    timeFrame?: "7days" | "30days" | "all"
  ): Promise<any> {
    // Get business profile first
    const { data: businessProfile } = await supabase
      .from("business_profiles")
      .select("id")
      .eq("user_id", userId)
      .single();

    if (!businessProfile) {
      return null;
    }

    // Get user's profile
    const { data: profile } = await supabase
      .from("linkinbio_profiles")
      .select("id")
      .eq("business_profile_id", businessProfile.id)
      .single();

    if (!profile) {
      return null;
    }

    // Build date filter
    let dateFilter = new Date();
    if (timeFrame === "7days") {
      dateFilter.setDate(dateFilter.getDate() - 7);
    } else if (timeFrame === "30days") {
      dateFilter.setDate(dateFilter.getDate() - 30);
    } else {
      dateFilter = new Date(0); // All time
    }

    // Get analytics data
    const { data: analytics } = await supabase
      .from("linkinbio_analytics")
      .select(`
        id,
        clicked_at,
        linkinbio_custom_links!inner (
          id,
          title,
          profile_id
        )
      `)
      .eq("linkinbio_custom_links.profile_id", profile.id)
      .gte("clicked_at", dateFilter.toISOString());

    return analytics;
  }

  // Helper methods
  private static mapDbProfileToLinkInBioProfile(
    profile: any,  // Using any temporarily due to type mismatch
    socialLinks: any[],
    customLinks: any[],
    bookingUrl?: string  // Optional booking_url for public profiles
  ): LinkInBioProfile {
    // Map background_type and background_value to backgroundColor/backgroundImage
    let backgroundColor: string | undefined;
    let backgroundImage: string | undefined;

    if (profile.background_type === 'solid') {
      backgroundColor = profile.background_value;
    } else if (profile.background_type === 'gradient') {
      backgroundColor = profile.background_value; // Gradient string
    } else if (profile.background_type === 'image') {
      backgroundImage = profile.background_value;
    }

    return {
      username: bookingUrl || '',  // Use booking_url as username
      displayName: profile.display_name,
      bio: profile.bio || undefined,
      profileImage: profile.avatar_url || undefined,  // Map avatar_url to profileImage
      socialLinks: socialLinks
        .filter((link) => link.is_active !== false)
        .map((link) => ({
          platform: link.platform as any,
          url: link.url,
        })),
      customLinks: customLinks
        .filter((link) => link.is_active !== false)
        .map((link) => ({
          id: link.id,
          title: link.title,
          url: link.url,
          icon: link.icon_value || undefined,  // Map icon_value to icon
          enabled: link.is_active !== false,
          order: link.order_index,
        })),
      theme: profile.theme || 'minimal',
      font: 'default',  // Not in DB, use default
      layout: 'business',  // Not in DB, use default
      buttonStyle: 'rounded',  // Not in DB, use default
      backgroundColor,
      backgroundImage,
      buttonColor: undefined,  // Not in DB
      textColor: undefined,  // Not in DB
      showBookingButton: profile.show_brand !== false,  // Map show_brand to showBookingButton
    };
  }

  // Check booking URL availability (for business profiles)
  static async isBookingUrlAvailable(bookingUrl: string): Promise<boolean> {
    const { data } = await supabase
      .from("business_profiles")
      .select("id")
      .eq("booking_url", bookingUrl)
      .single();

    return !data;
  }

  // Get business profile info for LinkInBio
  static async getBusinessInfo(userId: string): Promise<any> {
    const { data } = await supabase
      .from("business_profiles")
      .select("id, name, booking_url, website, instagram")
      .eq("user_id", userId)
      .single();

    return data;
  }
}